import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { EagGridStore } from '../store/createEagGridStore';
import type { RowData } from '../types/RowData';
type UseGridSelectionParams = {
  store: EagGridStore;
  rowSelectMode: 'NONE' | 'SINGLE' | 'MULTIPLE';
  selectedRowIds: Set<string>;
  rows: RowData[];
  onRowSelectionChange: ((rows: RowData[]) => void) | undefined;
};
type UseGridSelectionReturn = {
  handleToggleSelection: (rowId: string) => void;
  handleDeselectAll: () => void;
};

/**
 * Manages row selection logic and fires onRowSelectionChange notifications.
 *
 * Selection toggle delegates to the correct store method based on rowSelectMode.
 * The onRowSelectionChange effect fires on all selection paths (select-all,
 * deselect-all, row click, bulk action) — not just direct row clicks.
 */
export const useGridSelection = params => {
  const $ = _c(14);
  const onRowSelectionChangeRef = useRef(params.onRowSelectionChange);
  let t0;
  if ($[0] !== params.onRowSelectionChange) {
    t0 = () => {
      onRowSelectionChangeRef.current = params.onRowSelectionChange;
    };
    $[0] = params.onRowSelectionChange;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(t0);
  let t1;
  let t2;
  if ($[2] !== params.rows || $[3] !== params.selectedRowIds) {
    t1 = () => {
      if (!onRowSelectionChangeRef.current) {
        return;
      }
      const selectedRows = params.rows.filter(row => params.selectedRowIds.has(row.id));
      onRowSelectionChangeRef.current(selectedRows);
    };
    t2 = [params.selectedRowIds, params.rows];
    $[2] = params.rows;
    $[3] = params.selectedRowIds;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== params.rowSelectMode || $[7] !== params.store) {
    t3 = rowId => {
      if (params.rowSelectMode === "MULTIPLE") {
        params.store.toggleRowSelection(rowId);
      } else {
        if (params.rowSelectMode === "SINGLE") {
          params.store.setSingleSelection(rowId);
        }
      }
    };
    $[6] = params.rowSelectMode;
    $[7] = params.store;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleToggleSelection = t3;
  let t4;
  if ($[9] !== params.store) {
    t4 = () => params.store.deselectAll();
    $[9] = params.store;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleDeselectAll = t4;
  let t5;
  if ($[11] !== handleDeselectAll || $[12] !== handleToggleSelection) {
    t5 = {
      handleToggleSelection,
      handleDeselectAll
    };
    $[11] = handleDeselectAll;
    $[12] = handleToggleSelection;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};